import React from 'react';
import './RTButton.css';

const RTButton = ({
  children,
  type = "button", // "button", "submit", "link", or "icon"
  variant = "primary", // "primary" or "secondary"
  size = "normal", // "normal", "small", or "large"
  style = {},
  className = "",
  disabled = false,
  href,
  onClick,
  icon, // Icon component (for type="icon")
  iconMode = false, // New prop for minimal background width
  ...props
}) => {
  const isLink = type === "link";
  const isIcon = type === "icon";
  
  // Size configurations
  const sizeConfig = {
    large: { 
      padding: isIcon ? '12px' : '10px 18px', 
      fontSize: '16px', 
      minWidth: isIcon ? '48px' : '100px',
      iconSize: '24px'
    },
    normal: { 
      padding: isIcon ? '10px' : '6px 12px', 
      fontSize: '14px', 
      minWidth: isIcon ? '40px' : '80px',
      iconSize: '20px'
    },
    small: { 
      padding: isIcon ? '8px' : '4px 8px', 
      fontSize: '12px', 
      minWidth: isIcon ? '32px' : '60px',
      iconSize: '16px'
    }
  };
  
  const currentSize = sizeConfig[size] || sizeConfig.normal;
  
  const baseStyle = {
    border: (isLink || isIcon || iconMode) ? 'none' : 'none',
    borderRadius: isLink ? 0 : (isIcon ? '50%' : (iconMode ? '4px' : 6)),
    padding: isLink ? 0 : (iconMode ? '4px' : currentSize.padding),
    fontWeight: (isIcon || iconMode) ? 'normal' : 800,
    fontSize: currentSize.fontSize,
    minWidth: isLink ? 'auto' : (iconMode ? 'auto' : currentSize.minWidth),
    width: iconMode ? 'auto' : undefined,
    height: isIcon ? currentSize.minWidth : (iconMode ? 'auto' : 'auto'),
    boxShadow: (isLink || iconMode) ? 'none' : (isIcon ? '0 2px 8px rgba(21,60,127,0.15)' : '0 2px 8px rgba(21,60,127,0.08)'),
    transition: 'all 0.2s ease',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.7 : 1,
    textDecoration: isLink ? 'underline' : undefined,
    backgroundColor: (isLink || iconMode) ? 'transparent' : (variant === "primary" ? '#153C7F' : '#e0e0e0'),
    color: isLink
      ? (variant === "primary" ? '#153C7F' : '#007bff')
      : (iconMode ? '#6c757d' : (variant === "primary" ? '#fff' : '#153C7F')),
    display: (isIcon || iconMode) ? 'flex' : 'inline-block',
    alignItems: (isIcon || iconMode) ? 'center' : undefined,
    justifyContent: (isIcon || iconMode) ? 'center' : undefined,
    ...style
  };

  if (isIcon) {
    const IconComponent = icon || children;
    return (
      <button
        type="button"
        className={`rt-btn rt-btn-icon ${className}`}
        style={baseStyle}
        disabled={disabled}
        onClick={onClick}
        {...props}
      >
        {React.isValidElement(IconComponent) ? 
          React.cloneElement(IconComponent, { 
            size: currentSize.iconSize,
            style: { fontSize: currentSize.iconSize }
          }) : 
          IconComponent
        }
      </button>
    );
  }

  if (isLink) {
    return (
      <button
        className={`rt-btn ${iconMode ? 'rt-btn-icon-mode' : ''} ${className}`}
        style={baseStyle}
        onClick={disabled ? (e) => e.preventDefault() : onClick}
        aria-disabled={disabled}
        tabIndex={disabled ? -1 : 0}
        disabled={disabled}
        {...props}
      >
        {children}
      </button>
    );
  }

  return (
    <button
      type={type}
      className={`rt-btn ${iconMode ? 'rt-btn-icon-mode' : ''} ${className}`}
      style={baseStyle}
      disabled={disabled}
      onClick={onClick}
      {...props}
    >
      {children}
    </button>
  );
};

export default RTButton;